import React, { useEffect, useRef } from 'react';

import amplitude from 'amplitude-js';
import PopupNew from 'apps/auth/components/common/Popup/PopupNew';
import CashPickupLocationsPopup from 'apps/auth/components/send/popups/CashPickupLocationsPopup';
import MexicoCashChekOutPopup from 'apps/auth/components/send/popups/MexicoCashChekOutPopup';
import PickupBanksPopup from 'apps/auth/components/send/popups/PickupBanksPopup';
import PickupDocsPopup from 'apps/auth/components/send/popups/PickupDocsPopup';
import { getRecipientTypeParam, transactionMethods } from 'apps/auth/components/utils/AmplitudeUtils';
import { ADDITIONAL_INFO_SHOWN, PAYOUT_METHOD_GUIDE_SCREEN_SHOWN } from 'apps/auth/constants/amplitude/AmplitudeEvents';
import { PAYOUT_COUNTRY, PAYOUT_METHOD, RECIPIENT_TYPE } from 'apps/auth/constants/amplitude/AmplitudeParamName';
import { DELIVERY_CASH, DELIVERY_HOME } from 'apps/auth/constants/Constants';
import { DELIVERY_METHOD, TO_BANK_CODE } from 'apps/auth/constants/fieldsName';
import { VIETNAM } from 'apps/auth/constants/WellKnownCountries';
import { capitalizeFirstLetters, resolveArguments } from 'apps/auth/helpers/utils';
import { useAuth, useExtra, useFields, usePageMessages } from 'apps/auth/hooks/selectors';
import { DisclaimerService, VIETCOMBANK } from 'apps/auth/services/DisclaimerService';

import ClientSideDisclaimer from './ClientSideDisclaimer';
import ServerSideDisclaimer from './ServerSideDisclaimer';

function useBlanksAndDocs(popupRef) {
    const clickBanks = (e) => {
        e.preventDefault();
        popupRef.current?.open(<PickupBanksPopup popupRef={popupRef} />);
    };
    const clickUniteller = (e) => {
        e.preventDefault();
        popupRef.current?.open(<CashPickupLocationsPopup popupRef={popupRef} />);
    };

    const clickDocs = (e) => {
        e.preventDefault();
        popupRef.current?.open(<PickupDocsPopup popupRef={popupRef} />);
    };

    const clickMexicoCashChekOut = (e) => {
        e.preventDefault();
        popupRef.current?.open(<MexicoCashChekOutPopup popupRef={popupRef} />);
    };

    useEffect(() => {
        let elBanks, elDocs, mexicoCashCheckOut, unitellerLocation;
        if (__isBrowser__) {
            elBanks = document.querySelector('a[data-modal-id="modal-pickup-banks-block"]');
            if (elBanks) {
                elBanks.addEventListener('click', clickBanks);
            }

            unitellerLocation = document.querySelector('a[data-modal-id="modal-pickup-uniteller-block"]');
            if (unitellerLocation) {
                unitellerLocation.addEventListener('click', clickUniteller);
            }

            elDocs = document.querySelector('a[data-modal-id="modal-pickup-docs-block"]');
            if (elDocs) {
                elDocs.addEventListener('click', clickDocs);
            }

            mexicoCashCheckOut = document.querySelector('a[data-modal-id="modal-mexico-cash-chek-out"]');
            if (mexicoCashCheckOut) {
                mexicoCashCheckOut.addEventListener('click', clickMexicoCashChekOut);
            }
        }

        return () => {
            if (elBanks) {
                elBanks.removeEventListener('click', clickBanks);
            }
            if (elDocs) {
                elDocs.removeEventListener('click', clickDocs);
            }
            if (mexicoCashCheckOut) {
                mexicoCashCheckOut.removeEventListener('click', clickMexicoCashChekOut);
            }
            if (unitellerLocation) {
                unitellerLocation.removeEventListener('click', clickUniteller);
            }
        };
    });
}

const getTitleWithBankName = (toBankCode, title) => {
    if (toBankCode?.items && Array.isArray(toBankCode.items)) {
        const bankName = toBankCode.items.find((code) => code.value === toBankCode.value)?.text;
        return resolveArguments(title, capitalizeFirstLetters(bankName));
    }
};

const getClientSideTitle = (toBankCode, titleDisclaimer) => {
    let title = titleDisclaimer;
    if (toBankCode?.value) {
        title = getTitleWithBankName(toBankCode, titleDisclaimer);
    }
    return title;
};

const getServerSideTitle = (toBankCode, titleDisclaimer, toCountry, notificationNameTo, deliveryMethod) => {
    if (toCountry?.id === VIETNAM) {
        if (toBankCode?.value === VIETCOMBANK || deliveryMethod === DELIVERY_HOME) {
            return titleDisclaimer;
        } else {
            return getTitleWithBankName(toBankCode, titleDisclaimer);
        }
    } else {
        return resolveArguments(titleDisclaimer, notificationNameTo);
    }
};

const Disclaimer = (props) => {
    const { countryId } = useAuth();
    const popupRef = useRef(null);
    const { deliveryMethod, isDisclaimerFromServerSide, disclaimerFromServerSide, specialVietcomBankDisclaimerFromServerSide } = useExtra();
    useBlanksAndDocs(popupRef);
    const fields = useFields(props.formName);
    const { textDisclaimer, textDisclaimerVietcombank, titleDisclaimer, submitDisclaimer, notificationNameTo } = usePageMessages();
    const amplitudeDeliveryMethod = transactionMethods[deliveryMethod];

    useEffect(() => {
        amplitude.getInstance().logEvent(ADDITIONAL_INFO_SHOWN, {
            [PAYOUT_COUNTRY]: props.toCountry?.nameEn,
            [PAYOUT_METHOD]: amplitudeDeliveryMethod,
        });
        amplitude.getInstance().logEvent(PAYOUT_METHOD_GUIDE_SCREEN_SHOWN, {
            [PAYOUT_COUNTRY]: props.toCountry?.nameEn,
            [PAYOUT_METHOD]: amplitudeDeliveryMethod,
            [RECIPIENT_TYPE]: getRecipientTypeParam(props.recipientType),
        });
    }, []);

    const disclaimerContent = DisclaimerService.getDisclaimerText(
        textDisclaimer,
        textDisclaimerVietcombank,
        props.toCountry?.id,
        fields[DELIVERY_METHOD]?.value,
        fields,
        isDisclaimerFromServerSide,
        disclaimerFromServerSide,
        specialVietcomBankDisclaimerFromServerSide,
    );

    const getServerSideParagraphHeader = (section, i) => {
        const toBankCode = fields[TO_BANK_CODE];
        const toCountry = props.toCountry?.id;
        let title = section.header;
        if (toCountry === VIETNAM && toBankCode?.value !== VIETCOMBANK && i === 0 && deliveryMethod === DELIVERY_CASH) {
            title = getTitleWithBankName(toBankCode, section.header);
        }
        return title;
    };

    return (
        <>
            {isDisclaimerFromServerSide && disclaimerFromServerSide && Object.keys(disclaimerFromServerSide).length > 0 ? (
                <ServerSideDisclaimer
                    title={getServerSideTitle(fields[TO_BANK_CODE], disclaimerContent?.title, props.toCountry, notificationNameTo, deliveryMethod)}
                    content={disclaimerContent}
                    onButtonClick={props.clickAgree}
                    userCountry={countryId}
                    linkTo={props.backLink}
                    detailsClick={(e, bottomSheetBanks, bottomSheet) => {
                        e?.preventDefault();
                        if (bottomSheetBanks?.banks?.length > 0) {
                            popupRef.current?.open(<CashPickupLocationsPopup popupRef={popupRef} banksList={bottomSheetBanks?.banks} />);
                        } else if (bottomSheet?.text) {
                            popupRef.current?.open(<PickupDocsPopup popupRef={popupRef} docsList={bottomSheet} />);
                        }
                    }}
                    vietcomBankDisclaimer={specialVietcomBankDisclaimerFromServerSide}
                    getParagraphHeader={getServerSideParagraphHeader}
                />
            ) : (
                <ClientSideDisclaimer
                    title={getClientSideTitle(fields[TO_BANK_CODE], titleDisclaimer)}
                    text={disclaimerContent}
                    linkTo={props.backLink}
                    onButtonClick={props.clickAgree}
                    buttonCaption={submitDisclaimer}
                />
            )}
            <PopupNew ref={popupRef} />
        </>
    );
};

export default Disclaimer;
